import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, API, uploadFile, formatApiErrorDetail } from "../lib/api";
import { CodeBlock } from "../components/CodeBlock";
import { Layout } from "../components/Layout";
import { SupportAppEditor, SupportAppPreview } from "../components/SupportAppBuilder";
import { SwiftDialogEditor, SwiftDialogPreview } from "../components/SwiftDialogBuilder";
import { AiAssistPanel } from "../components/AiAssistPanel";
import { toast } from "sonner";
import { SlidersHorizontal, Lightning, FloppyDisk, Archive, FileCode, Terminal, UploadSimple, Eye, ClipboardText, Bell, ChatCenteredText, DownloadSimple, Plus, Trash, AppWindow, Folder, Image as ImageIcon, ArrowClockwise, DotsSixVertical, Package } from "@phosphor-icons/react";

export default function AppConfig() {
  const location = useLocation();
  const [apps, setApps] = useState([]);
  const [providers, setProviders] = useState([]);
  const [projects, setProjects] = useState([]);
  const [appId, setAppId] = useState(location.state?.appId || "supportapp");
  const [provider, setProvider] = useState("jamf");
  const [values, setValues] = useState({});
  const [result, setResult] = useState(null);
  const [tabIdx, setTabIdx] = useState(0);
  const [building, setBuilding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bundling, setBundling] = useState(false);
  const [fullPkg, setFullPkg] = useState(false);
  const [saveTarget, setSaveTarget] = useState("new");
  const [drag, setDrag] = useState(null); // { key, from, over }
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem("mdmforge_onboarding_cart") || "[]"); } catch { return []; }
  });
  const [downloadingCart, setDownloadingCart] = useState(false);

  useEffect(() => {
    localStorage.setItem("mdmforge_onboarding_cart", JSON.stringify(cart));
  }, [cart]);

  const addToBundle = () => {
    if (!appSpec) return;
    setCart((c) => [...c.filter((i) => i.app !== appId), { app: appId, name: appSpec.name, params: values }]);
    toast.success(`${appSpec.name} added to onboarding bundle`);
  };
  const removeFromBundle = (id) => setCart((c) => c.filter((i) => i.app !== id));
  const downloadOnboardingBundle = async () => {
    if (!cart.length) return;
    setDownloadingCart(true);
    try {
      const resp = await fetch(`${API}/appconfig/onboarding-bundle`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ provider, items: cart.map((i) => ({ app: i.app, params: i.params })) }),
      });
      if (!resp.ok) throw new Error((await resp.json().catch(() => ({}))).detail || "Bundle failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${provider}-onboarding-bundle.zip`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Onboarding bundle downloaded — ordered, ready to deploy");
    } catch (err) {
      toast.error(err.message || "Bundle failed");
    } finally {
      setDownloadingCart(false);
    }
  };

  useEffect(() => {
    api.get("/appconfig/meta").then((r) => setApps(r.data.apps)).catch(() => {});
    api.get("/providers").then((r) => setProviders(r.data)).catch(() => {});
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  const appSpec = useMemo(() => apps.find((a) => a.id === appId), [apps, appId]);

  useEffect(() => {
    if (!appSpec) return;
    const init = {};
    appSpec.params.forEach((p) => (init[p.key] = p.default));
    setValues(init);
    setResult(null);
    setTabIdx(0);
  }, [appSpec]);

  const setVal = (key, val) => setValues((v) => ({ ...v, [key]: val }));

  const handleUpload = async (key, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const tid = toast.loading("Uploading…");
    try {
      const d = await uploadFile(file);
      setVal(key, d.url);
      toast.success("Uploaded", { id: tid });
    } catch {
      toast.error("Upload failed", { id: tid });
    }
  };

  const build = async () => {
    setBuilding(true);
    setResult(null);
    try {
      const { data } = await api.post("/appconfig/build", { app: appId, provider, params: values });
      setResult(data);
      setTabIdx(0);
      toast.success("Generated — 0 tokens used");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Build failed");
    } finally {
      setBuilding(false);
    }
  };

  const aiFill = async (description, thenBuild) => {
    try {
      const { data } = await api.post("/appconfig/ai-suggest", { app: appId, description });
      const merged = { ...values, ...(data.params || {}) };
      setValues(merged);
      toast.success("AI configured the fields — review before building");
      if (thenBuild) {
        const { data: built } = await api.post("/appconfig/build", { app: appId, provider, params: merged });
        setResult(built);
        setTabIdx(0);
        toast.success("Generated — ready to review");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "AI could not configure that");
    }
  };

  const artifacts = result?.artifacts || [];
  const active = artifacts[tabIdx];

  const copyConfig = () => {
    navigator.clipboard.writeText(JSON.stringify({ app: appId, provider, params: values }, null, 2));
    toast.success("Config copied as JSON");
  };
  const importConfig = () => {
    const raw = window.prompt("Paste a saved config JSON:");
    if (!raw) return;
    try {
      const d = JSON.parse(raw);
      if (d.provider) setProvider(d.provider);
      if (d.app && apps.find((a) => a.id === d.app) && d.app !== appId) {
        setAppId(d.app);
        if (d.params) setTimeout(() => setValues((v) => ({ ...v, ...d.params })), 60);
      } else if (d.params) {
        setValues((v) => ({ ...v, ...d.params }));
      }
      toast.success("Config imported");
    } catch { toast.error("Invalid JSON"); }
  };

  const save = async () => {
    if (!active) return;
    setSaving(true);
    try {
      if (saveTarget === "new") {
        const { data } = await api.post("/projects", {
          title: `${result.name} (${active.label})`,
          provider: result.provider,
          output_type: active.type === "profile" ? "profile" : "script",
          prompt: `App config: ${appId}`,
          content: active.content, language: active.language, filename: active.filename,
          deploy_notes: result.deploy_notes,
        });
        setProjects((p) => [data, ...p]);
        toast.success(`Saved ${active.label} as new project`);
      } else {
        const { data } = await api.post(`/projects/${saveTarget}/versions`, {
          content: active.content, language: active.language, filename: active.filename,
          deploy_notes: result.deploy_notes, prompt: `App config: ${appId}`, note: `${result.name} (${active.label})`,
        });
        setProjects((list) => list.map((p) => (p.id === data.id ? data : p)));
        toast.success(`Saved new version to "${data.title}"`);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const downloadBundle = async () => {
    if (!artifacts.length) return;
    setBundling(true);
    try {
      const resp = await fetch(`${API}/bundle-multi`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({
          title: `${result.name} config`, provider: result.provider, deploy_notes: result.deploy_notes,
          files: artifacts.map((a) => ({ filename: a.filename, content: a.content })),
        }),
      });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${result.provider}-${appId}.zip`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Bundle downloaded");
    } catch {
      toast.error("Bundle download failed");
    } finally {
      setBundling(false);
    }
  };

  const downloadFullPackage = async () => {
    setFullPkg(true);
    try {
      const resp = await fetch(`${API}/appconfig/full-package`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ app: appId, provider, params: values }),
      });
      if (!resp.ok) {
        let msg = "Full package download failed";
        try { const j = await resp.json(); msg = j.detail || msg; } catch { /* non-json */ }
        toast.error(msg);
        return;
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${provider}-${appId}-offline.zip`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Self-contained package downloaded — hosted installer + baked config, installs offline");
    } catch {
      toast.error("Full package download failed");
    } finally {
      setFullPkg(false);
    }
  };

  const inputCls = "w-full bg-[#050505] border border-[#27272A] px-3 py-2 text-sm font-mono focus:border-[#00F0FF] focus:outline-none transition-colors duration-200";
  const labelCls = "block font-mono text-[10px] text-[#71717A] mb-1.5 uppercase tracking-wider";

  const renderField = (p) => {
    const v = values[p.key];
    if (p.type === "upload") {
      return (
        <div className="flex gap-2">
          <input data-testid={`appcfg-param-${p.key}`} value={v ?? ""} onChange={(e) => setVal(p.key, e.target.value)} className={inputCls} placeholder="paste URL or upload" />
          <label className="flex items-center gap-1.5 border border-[#27272A] px-3 text-xs text-[#A1A1AA] hover:border-[#00F0FF] hover:text-[#00F0FF] cursor-pointer transition-colors duration-200 whitespace-nowrap">
            <UploadSimple size={14} /> Upload
            <input type="file" accept="image/*" className="hidden" data-testid={`appcfg-upload-${p.key}`} onChange={(e) => handleUpload(p.key, e)} />
          </label>
        </div>
      );
    }
    if (p.type === "select") {
      return (
        <select data-testid={`appcfg-param-${p.key}`} value={v ?? ""} onChange={(e) => setVal(p.key, e.target.value)} className={inputCls}>
          {p.options.map((o) => (<option key={o} value={o}>{o}</option>))}
        </select>
      );
    }
    if (p.type === "bool") {
      return (
        <button type="button" data-testid={`appcfg-param-${p.key}`} onClick={() => setVal(p.key, !v)}
          className={`flex items-center gap-2 px-3 py-2 border text-xs transition-colors duration-200 ${v ? "border-[#00FF41] text-[#00FF41] bg-[#00FF41]/5" : "border-[#27272A] text-[#71717A]"}`}>
          <span className={`w-8 h-4 rounded-full relative transition-colors duration-200 ${v ? "bg-[#00FF41]/40" : "bg-[#27272A]"}`}>
            <span className={`absolute top-0.5 w-3 h-3 rounded-full transition-all duration-200 ${v ? "left-4 bg-[#00FF41]" : "left-0.5 bg-[#71717A]"}`} />
          </span>
          {v ? "Enabled" : "Disabled"}
        </button>
      );
    }
    if (p.type === "list") {
      const items = Array.isArray(v) ? v : [];
      const updateItem = (idx, key, val) =>
        setVal(p.key, items.map((it, i) => (i === idx ? { ...it, [key]: val } : it)));
      const addItem = () => {
        const blank = {};
        p.item_params.forEach((ip) => (blank[ip.key] = ip.default));
        setVal(p.key, [...items, blank]);
      };
      const removeItem = (idx) => setVal(p.key, items.filter((_, i) => i !== idx));
      const move = (from, to) => {
        if (from === to || from == null || to < 0 || to >= items.length) return;
        const next = [...items];
        const [m] = next.splice(from, 1);
        next.splice(to, 0, m);
        setVal(p.key, next);
      };
      return (
        <div className="space-y-3" data-testid={`appcfg-list-${p.key}`}>
          {items.map((it, idx) => (
            <div
              key={idx}
              onDragOver={(e) => { if (drag?.key === p.key) { e.preventDefault(); if (drag.over !== idx) setDrag({ ...drag, over: idx }); } }}
              onDrop={(e) => { e.preventDefault(); if (drag?.key === p.key) move(drag.from, idx); setDrag(null); }}
              className={`border bg-[#0f0f11] p-3 space-y-2 transition-colors duration-150 ${drag?.key === p.key && drag.over === idx && drag.from !== idx ? "border-[#00F0FF]" : "border-[#27272A]"} ${drag?.key === p.key && drag.from === idx ? "opacity-50" : ""}`}
              data-testid={`appcfg-list-${p.key}-item-${idx}`}
            >
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 font-mono text-[10px] text-[#71717A] uppercase tracking-wider">
                  <DotsSixVertical
                    size={15} weight="bold"
                    draggable
                    onDragStart={() => setDrag({ key: p.key, from: idx, over: idx })}
                    onDragEnd={() => setDrag(null)}
                    className="text-[#52525B] hover:text-[#00F0FF] cursor-grab active:cursor-grabbing transition-colors"
                    data-testid={`appcfg-list-${p.key}-drag-${idx}`}
                  />
                  #{idx + 1}
                </span>
                <div className="flex items-center gap-1.5">
                  <button type="button" title="Move up" disabled={idx === 0} data-testid={`appcfg-list-${p.key}-up-${idx}`} onClick={() => move(idx, idx - 1)}
                    className="text-[#71717A] hover:text-[#00F0FF] transition-colors disabled:opacity-20 disabled:cursor-not-allowed rotate-90"><ArrowClockwise size={13} /></button>
                  <button type="button" title="Move down" disabled={idx === items.length - 1} data-testid={`appcfg-list-${p.key}-down-${idx}`} onClick={() => move(idx, idx + 1)}
                    className="text-[#71717A] hover:text-[#00F0FF] transition-colors disabled:opacity-20 disabled:cursor-not-allowed -rotate-90"><ArrowClockwise size={13} /></button>
                  <button type="button" data-testid={`appcfg-list-${p.key}-remove-${idx}`} onClick={() => removeItem(idx)}
                    className="text-[#71717A] hover:text-[#FF5F56] transition-colors ml-1"><Trash size={14} /></button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {p.item_params.map((ip) => {
                  if (ip.type === "list") {
                    const sub = Array.isArray(it[ip.key]) ? it[ip.key] : [];
                    const updateSub = (si, sk, sv) => updateItem(idx, ip.key, sub.map((s, i) => (i === si ? { ...s, [sk]: sv } : s)));
                    const addSub = () => { const b = {}; ip.item_params.forEach((sp) => (b[sp.key] = sp.default)); updateItem(idx, ip.key, [...sub, b]); };
                    const removeSub = (si) => updateItem(idx, ip.key, sub.filter((_, i) => i !== si));
                    return (
                      <div key={ip.key} className="md:col-span-2 border-l-2 border-[#27272A] pl-3">
                        <label className="block font-mono text-[9px] text-[#52525B] mb-1.5 uppercase tracking-wider">{ip.label}</label>
                        <div className="space-y-2">
                          {sub.map((s, si) => (
                            <div key={si} className="flex items-end gap-2" data-testid={`appcfg-list-${p.key}-${idx}-${ip.key}-${si}`}>
                              {ip.item_params.map((sp) => (
                                <div key={sp.key} className="flex-1">
                                  <label className="block font-mono text-[8px] text-[#52525B] mb-1 uppercase tracking-wider">{sp.label}</label>
                                  {sp.type === "select" ? (
                                    <select data-testid={`appcfg-list-${p.key}-${idx}-${ip.key}-${si}-${sp.key}`} value={s[sp.key] ?? ""} onChange={(e) => updateSub(si, sp.key, e.target.value)} className={inputCls}>
                                      {sp.options.map((o) => (<option key={o} value={o}>{o}</option>))}
                                    </select>
                                  ) : (
                                    <input data-testid={`appcfg-list-${p.key}-${idx}-${ip.key}-${si}-${sp.key}`} value={s[sp.key] ?? ""} onChange={(e) => updateSub(si, sp.key, e.target.value)} className={inputCls} />
                                  )}
                                </div>
                              ))}
                              <button type="button" title="Remove" data-testid={`appcfg-list-${p.key}-${idx}-${ip.key}-remove-${si}`} onClick={() => removeSub(si)} className="text-[#71717A] hover:text-[#FF5F56] pb-2"><Trash size={13} /></button>
                            </div>
                          ))}
                          <button type="button" data-testid={`appcfg-list-${p.key}-${idx}-${ip.key}-add`} onClick={addSub} className="flex items-center gap-1.5 border border-[#27272A] text-[#A1A1AA] px-3 py-1.5 text-[11px] hover:border-[#00F0FF] hover:text-[#00F0FF] transition-colors"><Plus size={12} /> {ip.add_label || "Add"}</button>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div key={ip.key}>
                      <label className="block font-mono text-[9px] text-[#52525B] mb-1 uppercase tracking-wider">{ip.label}</label>
                      {ip.type === "select" ? (
                        <select data-testid={`appcfg-list-${p.key}-${idx}-${ip.key}`} value={it[ip.key] ?? ""} onChange={(e) => updateItem(idx, ip.key, e.target.value)} className={inputCls}>
                          {ip.options.map((o) => (<option key={o} value={o}>{o}</option>))}
                        </select>
                      ) : ip.type === "bool" ? (
                        <button type="button" data-testid={`appcfg-list-${p.key}-${idx}-${ip.key}`} onClick={() => updateItem(idx, ip.key, !it[ip.key])}
                          className={`flex items-center gap-2 px-3 py-2 border text-xs w-full ${it[ip.key] ? "border-[#00FF41] text-[#00FF41] bg-[#00FF41]/5" : "border-[#27272A] text-[#71717A]"}`}>
                          {it[ip.key] ? "Yes" : "No"}
                        </button>
                      ) : (
                        <input data-testid={`appcfg-list-${p.key}-${idx}-${ip.key}`} value={it[ip.key] ?? ""} onChange={(e) => updateItem(idx, ip.key, e.target.value)} className={inputCls} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          <button type="button" data-testid={`appcfg-list-${p.key}-add`} onClick={addItem}
            className="flex items-center gap-2 border border-[#00F0FF] text-[#00F0FF] px-4 py-2 text-xs hover:bg-[#00F0FF]/10 transition-colors duration-200">
            <Plus size={14} /> {p.add_label || "Add item"}
          </button>
        </div>
      );
    }
    return (
      <input data-testid={`appcfg-param-${p.key}`} type={p.type === "number" ? "number" : "text"}
        value={v ?? ""} onChange={(e) => setVal(p.key, e.target.value)} className={inputCls} />
    );
  };

  return (
    <Layout>
      <div className="border-b border-[#27272A] px-8 py-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl tracking-tight flex items-center gap-2">
            <SlidersHorizontal size={22} className="text-[#00F0FF]" /> Deploy Software
          </h1>
          <p className="text-sm text-[#A1A1AA] font-mono mt-1">
            Visually configure any catalog tool — profile + deploy script, 0 LLM tokens.
          </p>
        </div>
        <span className="hidden md:flex items-center gap-2 font-mono text-xs text-[#00FF41] border border-[#00FF41]/40 bg-[#00FF41]/5 px-3 py-1.5">
          <Lightning size={13} weight="fill" /> 0 LLM TOKENS
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-px bg-[#27272A]">
        <div className="bg-[#0A0A0B] p-8 space-y-6">
          {/* Software picker */}
          <div>
            <label className={labelCls}>Software</label>
            <select data-testid="appcfg-software-select" value={appId} onChange={(e) => setAppId(e.target.value)} className={inputCls}>
              {apps.map((a) => (<option key={a.id} value={a.id}>{a.name} — {a.kind === "profile" ? "profile" : "deploy script"}</option>))}
            </select>
          </div>
          <div>
            <label className={labelCls}>MDM Provider</label>
            <div className="flex flex-wrap gap-2">
              {providers.map((p) => (
                <button key={p.id} data-testid={`appcfg-provider-${p.id}`} onClick={() => setProvider(p.id)}
                  className={`flex items-center gap-2 px-3 py-2 border text-xs transition-colors duration-200 ${provider === p.id ? "border-[#00F0FF] text-[#00F0FF]" : "border-[#27272A] text-[#A1A1AA] hover:border-[#3f3f46]"}`}>
                  <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />{p.name}
                </button>
              ))}
            </div>
          </div>

          {appSpec && (
            <AiAssistPanel
              testid="appcfg-ai"
              placeholder={`Describe what you want ${appSpec.name} to do — e.g. "enforce updates within 14 days with a friendly reminder"`}
              hint="AI fills the fields below — review, then build."
              onFill={(d) => aiFill(d, false)}
              onGenerate={(d) => aiFill(d, true)}
            />
          )}

          {/* Params */}
          {appSpec && appSpec.custom_ui === "supportapp" ? (
            <SupportAppEditor values={values} setVal={setVal} handleUpload={handleUpload} />
          ) : appSpec && appSpec.custom_ui === "swiftdialog" ? (
            <SwiftDialogEditor values={values} setVal={setVal} handleUpload={handleUpload} />
          ) : appSpec && appSpec.params.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="appcfg-params">
              {appSpec.params.map((p) => (
                <div key={p.key} className={p.type === "list" ? "md:col-span-2" : ""}>
                  <label className={labelCls}>{p.label}</label>
                  {renderField(p)}
                </div>
              ))}
            </div>
          ) : null}
          {appSpec && appSpec.custom_ui !== "supportapp" && appSpec.params.length === 0 && (
            <p className="text-xs text-[#71717A] font-mono">No parameters — this tool builds a ready-to-run script.</p>
          )}

          <div className="flex flex-wrap gap-3">
            <button data-testid="build-appcfg-btn" onClick={build} disabled={building}
              className="flex items-center gap-2 bg-[#00F0FF] text-[#0A0A0B] font-medium px-6 py-3 text-sm hover:bg-[#00d4e0] transition-colors duration-200 disabled:opacity-50">
              <Lightning size={16} weight="fill" /> {building ? "Building…" : "Build config (0 tokens)"}
            </button>
            <button data-testid="appcfg-import-btn" onClick={importConfig}
              className="flex items-center gap-2 border border-[#27272A] text-[#A1A1AA] px-4 py-3 text-sm hover:border-[#00F0FF] hover:text-[#00F0FF] transition-colors duration-200">
              <ClipboardText size={16} /> Import config
            </button>
            <button data-testid="appcfg-export-btn" onClick={copyConfig}
              className="flex items-center gap-2 border border-[#27272A] text-[#A1A1AA] px-4 py-3 text-sm hover:border-[#00F0FF] hover:text-[#00F0FF] transition-colors duration-200">
              <ClipboardText size={16} /> Copy config
            </button>
            <button data-testid="appcfg-add-bundle-btn" onClick={addToBundle} disabled={!appSpec}
              title="Add this configured tool to the New-Mac onboarding bundle"
              className="flex items-center gap-2 border border-[#B37FEB] text-[#C79BF0] px-4 py-3 text-sm hover:bg-[#B37FEB]/10 transition-colors duration-200 disabled:opacity-50">
              <Plus size={16} /> Add to onboarding bundle
            </button>
          </div>

          {cart.length > 0 && (
            <div className="border border-[#B37FEB]/40 bg-[#B37FEB]/5 p-4" data-testid="onboarding-cart">
              <div className="flex items-center justify-between mb-3">
                <span className="font-mono text-[10px] uppercase tracking-wider text-[#C79BF0] flex items-center gap-2">
                  <Package size={14} /> New-Mac Onboarding Bundle · {cart.length} tool{cart.length === 1 ? "" : "s"}
                </span>
                <button data-testid="onboarding-clear-btn" onClick={() => setCart([])} className="text-[10px] font-mono text-[#71717A] hover:text-[#FF5F56]">Clear all</button>
              </div>
              <div className="flex flex-wrap gap-2 mb-3">
                {cart.map((i) => (
                  <span key={i.app} data-testid={`onboarding-item-${i.app}`} className="flex items-center gap-1.5 bg-[#0f0f11] border border-[#27272A] px-2.5 py-1 text-[11px] text-[#FAFAFA]">
                    {i.name}
                    <button data-testid={`onboarding-remove-${i.app}`} onClick={() => removeFromBundle(i.app)} className="text-[#71717A] hover:text-[#FF5F56]"><Trash size={11} /></button>
                  </span>
                ))}
              </div>
              <button data-testid="onboarding-download-btn" onClick={downloadOnboardingBundle} disabled={downloadingCart}
                className="flex items-center gap-2 bg-[#B37FEB] text-[#0A0A0B] font-medium px-5 py-2.5 text-sm hover:bg-[#a06ede] transition-colors duration-200 disabled:opacity-50">
                <DownloadSimple size={16} weight="fill" /> {downloadingCart ? "Building bundle…" : "Download onboarding bundle (ordered)"}
              </button>
            </div>
          )}

          {result && active && (
            <div className="space-y-4" data-testid="appcfg-output">
              <div className="flex border border-[#27272A] w-fit">
                {artifacts.map((a, i) => (
                  <button key={a.filename} data-testid={`appcfg-tab-${a.type}`} onClick={() => setTabIdx(i)}
                    className={`flex items-center gap-2 px-4 py-2 text-sm ${i > 0 ? "border-l border-[#27272A]" : ""} ${tabIdx === i ? "bg-[#00F0FF]/10 text-[#00F0FF]" : "text-[#A1A1AA]"}`}>
                    {a.type === "profile" ? <FileCode size={15} /> : <Terminal size={15} />} {a.label}
                  </button>
                ))}
              </div>
              <div className="flex flex-col md:flex-row gap-3">
                <select data-testid="appcfg-save-target" value={saveTarget} onChange={(e) => setSaveTarget(e.target.value)} className={inputCls + " flex-1"}>
                  <option value="new">＋ New project</option>
                  {projects.map((p) => (<option key={p.id} value={p.id}>↳ Add version to: {p.title}</option>))}
                </select>
                <div className="flex gap-3">
                  <button data-testid="appcfg-save-btn" onClick={save} disabled={saving} className="flex items-center gap-2 border border-[#00FF41] text-[#00FF41] px-4 py-2 text-sm hover:bg-[#00FF41]/10 transition-colors duration-200 disabled:opacity-50"><FloppyDisk size={16} /> {saving ? "…" : `Save ${active.label}`}</button>
                  <button data-testid="appcfg-bundle-btn" onClick={downloadBundle} disabled={bundling} className="flex items-center gap-2 border border-[#0A84FF] text-[#0A84FF] px-4 py-2 text-sm hover:bg-[#0A84FF]/10 transition-colors duration-200 disabled:opacity-50"><Archive size={16} /> {bundling ? "…" : "Bundle all"}</button>
                  <button data-testid="appcfg-fullpkg-btn" onClick={downloadFullPackage} disabled={fullPkg} title="Download a self-contained ZIP: our hosted installer + your baked-in config + an offline install.sh. Nothing is pulled from GitHub at install time." className="flex items-center gap-2 border border-[#00FF41] text-[#00FF41] px-4 py-2 text-sm hover:bg-[#00FF41]/10 transition-colors duration-200 disabled:opacity-50"><Package size={16} /> {fullPkg ? "Building package…" : "Download self-contained package"}</button>
                </div>
              </div>
              <CodeBlock code={active.content} language={active.language} filename={active.filename} />
            </div>
          )}
        </div>

        {/* Live preview */}
        <div className="bg-[#0A0A0B] p-8">
          <label className={labelCls + " flex items-center gap-1.5"}><Eye size={12} /> Live preview</label>
          <div className="mt-3" data-testid="appcfg-preview">
            {appId === "supportapp" ? (
              <SupportAppPreview values={values} />
            ) : appId === "swiftdialog" ? (
              <SwiftDialogPreview values={values} />
            ) : appId === "nudge" ? (
              <div className="border border-[#27272A] bg-[#0f0f11]" data-testid="appcfg-nudge-preview">
                <div className="h-6 bg-[#1c1c1f] border-b border-[#27272A] flex items-center px-3 gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F56]" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <Bell size={26} weight="fill" className="text-[#0A84FF]" />
                    <span className="text-[#FAFAFA] text-sm font-medium">{values.main_header || "A macOS update is required"}</span>
                  </div>
                  <div className="space-y-2 text-[11px] font-mono">
                    <div className="flex justify-between border border-[#27272A] px-3 py-2"><span className="text-[#71717A]">Required macOS</span><span className="text-[#FAFAFA]">{values.required_os || "—"}</span></div>
                    <div className="flex justify-between border border-[#27272A] px-3 py-2"><span className="text-[#71717A]">Install by</span><span className="text-[#FAFAFA]">{(values.required_date || "").split("T")[0] || "—"}</span></div>
                  </div>
                  <div className="flex items-center gap-2 mt-4">
                    {values.about_url ? <div className="text-[11px] text-[#0A84FF] border border-[#0A84FF]/40 px-3 py-2">More Info</div> : null}
                    <div className="text-[11px] text-white bg-[#0A84FF] px-4 py-2 ml-auto">Update Now</div>
                  </div>
                </div>
              </div>
            ) : appId === "dockutil" ? (
              <div data-testid="appcfg-dockutil-preview">
                <div className="border border-[#27272A] bg-gradient-to-b from-[#1b2735] to-[#0f0f11] rounded-lg p-4 min-h-[180px] flex flex-col justify-end">
                  <div className="flex items-end justify-center gap-2 bg-[#ffffff10] border border-[#ffffff1a] rounded-2xl px-3 py-2 backdrop-blur-sm mx-auto">
                    {(Array.isArray(values.items) ? values.items : []).map((it, i) => {
                      if (it.kind === "Spacer") return <div key={i} className="w-4" />;
                      const isFolder = it.kind === "Folder";
                      const nm = (it.label || (it.path || "").split("/").pop() || "").replace(/\.app$/, "");
                      return (
                        <div key={i} className="flex flex-col items-center gap-1 group" title={it.path}>
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-transform duration-200 group-hover:-translate-y-1 ${isFolder ? "bg-[#3a3f4b]" : "bg-gradient-to-br from-[#00F0FF]/30 to-[#0A84FF]/40 border border-[#00F0FF]/40"}`}>
                            {isFolder ? <Folder size={20} weight="fill" className="text-[#c7c7cc]" /> : <AppWindow size={18} weight="fill" className="text-[#00F0FF]" />}
                          </div>
                          <span className="text-[8px] text-[#c7c7cc] max-w-[44px] truncate">{nm}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-2 text-[10px] font-mono text-[#71717A]">
                  <span className={`px-2 py-0.5 border ${values.remove_all ? "border-[#FF5F56]/40 text-[#FF9E99]" : "border-[#27272A]"}`}>{values.remove_all ? "clean rebuild" : "append to Dock"}</span>
                  <span className="px-2 py-0.5 border border-[#27272A]">{(Array.isArray(values.items) ? values.items : []).length} items</span>
                </div>
              </div>
            ) : appId === "desktoppr" ? (
              <div data-testid="appcfg-desktoppr-preview">
                <div className="relative border border-[#27272A] rounded-lg overflow-hidden aspect-video bg-[#0f0f11] flex items-center justify-center" style={values.bg_color ? { background: `#${values.bg_color}` } : {}}>
                  {values.wallpaper_url ? (
                    <img src={values.wallpaper_url} alt="wallpaper" className="w-full h-full"
                      style={{ objectFit: values.scale === "fit" ? "contain" : values.scale === "center" ? "none" : values.scale === "stretch" ? "fill" : "cover" }}
                      onError={(e) => (e.currentTarget.style.display = "none")} />
                  ) : (
                    <div className="text-center text-[#52525B]"><ImageIcon size={30} className="mx-auto mb-2" /><span className="text-[10px] font-mono">paste or upload a wallpaper</span></div>
                  )}
                  <div className="absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-1 bg-[#00000055] rounded-lg px-1.5 py-1 backdrop-blur-sm">
                    {[0, 1, 2].map((i) => <div key={i} className="w-4 h-4 rounded bg-white/40" />)}
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-2 text-[10px] font-mono text-[#71717A]">
                  <span className="px-2 py-0.5 border border-[#27272A] text-[#00F0FF]">{values.scale || "fill"}</span>
                  <span className="px-2 py-0.5 border border-[#27272A]">{values.all_screens ? "all displays" : "main display"}</span>
                </div>
              </div>
            ) : appId === "super" ? (
              <div className="border border-[#27272A] bg-[#0f0f11]" data-testid="appcfg-super-preview">
                <div className="h-6 bg-[#1c1c1f] border-b border-[#27272A] flex items-center px-3 gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F56]" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <ArrowClockwise size={24} weight="bold" className="text-[#00F0FF]" />
                    <span className="text-[#FAFAFA] text-sm font-medium">macOS updates are enforced</span>
                  </div>
                  <div className="space-y-2 text-[11px] font-mono">
                    <div className="flex justify-between border border-[#27272A] px-3 py-2"><span className="text-[#71717A]">Soft deadline</span><span className="text-[#FAFAFA]">{values.DeadlineDaysSoft || "—"} days / {values.DeadlineCountSoft || "—"} defers</span></div>
                    <div className="flex justify-between border border-[#FF5F56]/30 px-3 py-2"><span className="text-[#71717A]">Hard deadline</span><span className="text-[#FF9E99]">{values.DeadlineDaysHard || "—"} days / {values.DeadlineCountHard || "—"} defers</span></div>
                    <div className="flex justify-between border border-[#27272A] px-3 py-2"><span className="text-[#71717A]">Major upgrades</span><span className="text-[#FAFAFA]">{values.InstallMacOSMajorUpgrades ? `on${values.InstallMacOSMajorVersionTarget ? ` → ${values.InstallMacOSMajorVersionTarget}` : ""}` : "off"}</span></div>
                  </div>
                  <div className="mt-4 flex items-center gap-2">
                    <div className="text-[11px] text-[#A1A1AA] border border-[#27272A] px-3 py-2">Defer ({(values.DeferralTimerMenu || "").split(",")[0] || "60"}m)</div>
                    <div className="text-[11px] bg-[#00F0FF] px-4 py-2 ml-auto" style={{ color: "#0A0A0B" }}>Update Now</div>
                  </div>
                </div>
              </div>
            ) : appId === "depnotify" ? (
              <div className="border border-[#27272A] bg-[#0f0f11] p-6" data-testid="appcfg-depnotify-preview">
                {values.image_url ? (
                  <img src={values.image_url} alt="banner" className="h-10 mx-auto mb-4 object-contain" onError={(e) => (e.currentTarget.style.display = "none")} />
                ) : (
                  <div className="w-12 h-12 rounded-full border-2 border-[#00F0FF] flex items-center justify-center mx-auto mb-4"><DownloadSimple size={22} className="text-[#00F0FF]" /></div>
                )}
                <div className="text-[#FAFAFA] text-sm font-medium text-center mb-1">{values.title || "Setting up your Mac"}</div>
                <div className="text-[10px] text-[#71717A] text-center mb-4">{values.main_text || ""}</div>
                {(() => {
                  const steps = (Array.isArray(values.steps) ? values.steps : []).filter((s) => (s.label || "").trim());
                  const pct = steps.length ? Math.round((1 / steps.length) * 100) : 40;
                  return (
                    <>
                      <div className="h-1.5 bg-[#27272A] rounded-full overflow-hidden mb-2"><div className="h-full bg-[#00F0FF] transition-all duration-300" style={{ width: `${pct}%` }} /></div>
                      <div className="text-[10px] text-[#A1A1AA] font-mono text-center mb-3">{steps[0]?.label || "Working…"}</div>
                      <div className="space-y-1">
                        {steps.map((s, i) => (
                          <div key={i} className="flex items-center gap-2 text-[10px] font-mono">
                            <span className={`w-1.5 h-1.5 rounded-full ${i === 0 ? "bg-[#00F0FF]" : "bg-[#3f3f46]"}`} />
                            <span className={i === 0 ? "text-[#FAFAFA]" : "text-[#71717A]"}>{s.label}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  );
                })()}
              </div>
            ) : (
              <div className="border border-[#27272A] bg-[#0f0f11] p-5" data-testid="appcfg-summary">
                <div className="flex items-center gap-2 pb-3 border-b border-[#27272A] mb-3">
                  {appSpec?.kind === "profile" ? <FileCode size={16} className="text-[#00F0FF]" /> : <Terminal size={16} className="text-[#00F0FF]" />}
                  <span className="text-[#FAFAFA] text-sm font-medium">{appSpec?.name}</span>
                  <span className="ml-auto font-mono text-[10px] text-[#71717A] border border-[#27272A] px-2 py-0.5">
                    {appSpec?.kind === "profile" ? ".mobileconfig" : "deploy .sh"}
                  </span>
                </div>
                {appSpec?.domain && (
                  <div className="text-[10px] text-[#71717A] font-mono mb-3">domain · {appSpec.domain}</div>
                )}
                <div className="space-y-1.5">
                  {(appSpec?.params || []).map((p) => {
                    const v = values[p.key];
                    let shown;
                    if (p.type === "bool") shown = v ? "on" : "off";
                    else if (p.type === "list") shown = `${Array.isArray(v) ? v.length : 0} item${(Array.isArray(v) ? v.length : 0) === 1 ? "" : "s"}`;
                    else shown = v === "" || v == null ? "—" : String(v);
                    return (
                      <div key={p.key} className="flex items-start justify-between gap-3 text-[11px] font-mono">
                        <span className="text-[#71717A] truncate">{p.label}</span>
                        <span className="text-[#FAFAFA] text-right truncate max-w-[55%]">{shown}</span>
                      </div>
                    );
                  })}
                  {appSpec?.params.length === 0 && (
                    <div className="text-[11px] text-[#A1A1AA] font-mono">Zero-config — generates a ready-to-run script.</div>
                  )}
                </div>
              </div>
            )}
          </div>
          <p className="text-[10px] text-[#71717A] font-mono mt-4 leading-relaxed">
            Live summary of your configuration. Build to generate the profile and/or deploy script.
          </p>
        </div>
      </div>
    </Layout>
  );
}
